import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  fetchOverview,
  fetchDetail,
  fetchDevices,
  fetchLocations,
  fetchFlowList,
} from '@/api/dashboard'
import { getDeviceStatus } from '@/utils/deviceStatus'

export const COUNTRY_META = {
  KR: { name: 'Korea', flag: 'kr' },
  ID: { name: 'Indonesia', flag: 'id' },
  TH: { name: 'Thailand', flag: 'th' },
  TR: { name: 'Turkey', flag: 'tr' },
  MY: { name: 'Malaysia', flag: 'my' },
  IN: { name: 'India', flag: 'in' },
  VN: { name: 'Vietnam', flag: 'vn' },
  US: { name: 'USA', flag: 'us' },
}

export const useDashboardStore = defineStore('dashboard', () => {
  // ── 선택 상태 ──
  const selectedCountry = ref(null)
  const selectedCustomer = ref(null)

  // ── 데이터 ──
  const overview = ref([])
  const detail = ref([])
  const devices = ref([])
  const locations = ref([])
  // 유량(flow) 모니터링 지점 — devices 와는 별개 데이터 소스 (api/API21/flow/list)
  const flowList = ref([])
  const flowLoading = ref(false)
  // 유량 정렬 옵션 — 추후 UI 에서 노출 시 갱신
  const flowSort = ref('')
  // 지점 상태 필터 — 'A' Active | 'I' Inactive | '' All. press/leak/flow 목록 조회에 공통 적용
  //   기본값 'A' — 기존(하드코딩 type=A) 동작 유지
  const pointStatus = ref('A')
  // 진행중인 flow fetch 취소용
  let flowAbort = null

  // ── 장비 유형 탭 ──
  const activeDeviceType = ref('all') // 'all' | 'pressure' | 'leak' | 'flow'

  // ── 테이블 필터 ──
  const tableSearch = ref('')
  // 상태별 다중-가시성 상태 (전체 visible = 모두 표시)
  //   - 사이드바 상태 박스 클릭 = 그 상태만 단독 선택(exclusive). 같은 박스 재클릭 = 전체 복귀
  //   - 지도 범례 상태 클릭 = 개별 토글(체크박스처럼 멀티-선택)
  //   - DeviceTable, MapPanel 모두 이 상태를 단일 진실 원천으로 사용
  const tableStatusVisible = ref({ abnormal: true, caution: true, normal: true, delay: true })
  const visibleColumns = ref(['id', 'name', 'location', 'status', 'lastUpdate', 'uptime'])

  // ── AI 채팅 ──
  const aiMessages = ref([
    {
      role: 'ai',
      text: 'AI 에이전트: 장비 모니터링을 시작합니다. 이상 장비가 감지되면 알려드리겠습니다.',
      time: new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }),
    },
  ])

  // ── UI 상태 ──
  const loading = ref(false)
  const error = ref(null)

  // ── Device chart modal ──
  const selectedDevice = ref(null)
  // 선택 출처 — '' / 'map' / 'list' 등 (탭 자동 전환 분기용)
  const selectedDeviceSource = ref('')

  // ── 지도 포커스 ──
  const mapFocusDevice = ref(null)

  // ── 차트 비교 슬롯 — DeviceChartPanel 의 slotsByType 와 동기화 ──
  //   지도 MapPanel 에서 활성 유형 슬롯에 들어간 디바이스에 1/2/3 뱃지 표시용
  //   값: { pressure: [id|null, id|null, id|null], leak: [...], flow: [...] }
  const chartSlotsByType = ref({
    pressure: [null, null, null],
    leak:     [null, null, null],
    flow:     [null, null, null],
  })
  // 슬롯 지도 표시 여부(전체) — 지도 위 1/2/3 뱃지(슬롯 지점 표시)를 한 번에 켜고 끔. true=표시, false=숨김
  const slotMapVisible = ref(true)
  function setChartSlot(type, idx, deviceId) {
    if (!['pressure', 'leak', 'flow'].includes(type)) return
    if (idx < 0 || idx > 2) return
    const arr = [...(chartSlotsByType.value[type] || [null, null, null])]
    arr[idx] = deviceId == null ? null : String(deviceId)
    chartSlotsByType.value = { ...chartSlotsByType.value, [type]: arr }
  }
  function toggleSlotMapVisible() {
    slotMapVisible.value = !slotMapVisible.value
  }
  // 차트 비교 슬롯 전체 초기화 — 페이지 진입 시 마커 뱃지 리셋용
  function resetChartSlots() {
    chartSlotsByType.value = {
      pressure: [null, null, null],
      leak:     [null, null, null],
      flow:     [null, null, null],
    }
    slotMapVisible.value = true
  }

  // 차트 크게보기 — 대시보드 우측 패널을 화면 전체로 확장 (관리자 전용)
  //   DeviceTable 의 확대 아이콘에서 toggle, DashboardView 에서 watch
  //   닫힐 때 Multi Chart 모드/선택값을 함께 정리 — DataChartPanel 의 watcher 는
  //   v-if 로 컴포넌트가 unmount 되며 누락될 수 있어 store setter 에서 직접 처리
  const chartExpanded = ref(false)
  function setChartExpanded(v) {
    const next = !!v
    chartExpanded.value = next
    if (!next) {
      // 목록의 체크박스가 즉시 사라지도록 모드 OFF + 선택값 초기화
      multiChartMode.value = false
      multiChartSelectedIds.value = new Set()
    }
  }

  // Multi Chart 모드 — 압력 지점상세 확대 > Multi Chart 탭일 때만 true
  //   DeviceTable 가 이 플래그를 보고 압력 행에 체크박스를 노출
  //   multiChartSelectedIds: 비교 대상 m2_point_id 의 Set (string). 최대 20개
  const MULTI_CHART_MAX = 20
  const multiChartMode = ref(false)
  const multiChartSelectedIds = ref(new Set())
  function setMultiChartMode(v) { multiChartMode.value = !!v }
  function toggleMultiChartId(id) {
    if (id == null) return
    const key = String(id)
    const next = new Set(multiChartSelectedIds.value)
    if (next.has(key)) {
      next.delete(key)
    } else {
      if (next.size >= MULTI_CHART_MAX) return // 최대치 초과 — 추가 안 함
      next.add(key)
    }
    multiChartSelectedIds.value = next
  }
  function setMultiChartIds(ids) {
    const arr = Array.isArray(ids) ? ids : []
    multiChartSelectedIds.value = new Set(arr.slice(0, MULTI_CHART_MAX).map(String))
  }
  function clearMultiChartIds() { multiChartSelectedIds.value = new Set() }

  // ── 대시보드 전역 장치 리스트 (press/leak/flow 합본) ──
  const allDeviceList = ref([])
  function setAllDevices(list) {
    allDeviceList.value = Array.isArray(list) ? list : []
  }
  function findDeviceByPointId(id) {
    if (id == null) return null
    const pid = String(id)
    return allDeviceList.value.find(d =>
      String(d?.m2_point_id) === pid ||
      String(d?.point_id)    === pid ||
      String(d?.id)          === pid,
    ) || null
  }

  // ─────────────────────────────────────────
  // Computed
  // ─────────────────────────────────────────

  /** 통계 계산 헬퍼 — 장비 배열 → 유형별 카운트 */
  function calcStatsFromDevices(devList, type) {
    const isLeak = type === 'leak'
    const list = devList.filter(d => isLeak ? d.measure_type === 'leak' : d.measure_type !== 'leak')
    const counts = { total: list.length, abnormal: 0, caution: 0, normal: 0, unknown: 0 }
    for (const d of list) {
      const st = getDisplayStatus(d)
      if (st === 'abnormal') counts.abnormal++
      else if (st === 'caution') counts.caution++
      else if (st === 'normal') counts.normal++
      else counts.unknown++
    }
    return counts
  }

  /** flow 전용 통계 — flowList 기반 (상태 필드 없으면 모두 normal 로 집계, 'block_status' 등 있으면 매핑) */
  function calcFlowStats() {
    const list = flowList.value || []
    const counts = { total: list.length, abnormal: 0, caution: 0, normal: 0, unknown: 0 }
    for (const d of list) {
      const st = getDisplayStatus(d)
      if (counts[st] !== undefined) counts[st]++
      else counts.unknown++
    }
    return counts
  }

  /** 총괄 현황 통계 — 고객사 선택 시 devices 기반, 전체 시 overview 기반 */
  const globalStats = computed(() => {
    const isCustomerSelected =
      selectedCustomer.value && selectedCustomer.value !== '__all__'

    if (isCustomerSelected) {
      if (activeDeviceType.value === 'flow') return calcFlowStats()
      if (activeDeviceType.value === 'all') {
        const counts = { total: devices.value.length, abnormal: 0, caution: 0, normal: 0, unknown: 0 }
        for (const d of devices.value) { const st = getDisplayStatus(d); counts[st] = (counts[st] || 0) + 1 }
        return counts
      }
      return calcStatsFromDevices(devices.value, activeDeviceType.value)
    }

    if (activeDeviceType.value === 'all') {
      const pressure = {
        total: overview.value.reduce((s, o) => s + o.total_points, 0),
        abnormal: overview.value.reduce((s, o) => s + o.abnormal_count, 0),
        caution: overview.value.reduce((s, o) => s + o.caution_count, 0),
        normal: overview.value.reduce((s, o) => s + o.normal_count, 0),
        unknown: overview.value.reduce((s, o) => s + o.unknown_count, 0),
      }
      const leak = {
        total: overview.value.reduce((s, o) => s + o.leak_total_points, 0),
        abnormal: overview.value.reduce((s, o) => s + o.leak_abnormal_count, 0),
        caution: 0,
        normal: overview.value.reduce((s, o) => s + o.leak_normal_count, 0),
        unknown: overview.value.reduce((s, o) => s + o.leak_unknown_count, 0),
      }
      return {
        total: pressure.total + leak.total,
        abnormal: pressure.abnormal + leak.abnormal,
        caution: pressure.caution,
        normal: pressure.normal + leak.normal,
        unknown: pressure.unknown + leak.unknown,
      }
    }
    if (activeDeviceType.value === 'pressure') {
      return {
        total: overview.value.reduce((s, o) => s + o.total_points, 0),
        abnormal: overview.value.reduce((s, o) => s + o.abnormal_count, 0),
        caution: overview.value.reduce((s, o) => s + o.caution_count, 0),
        normal: overview.value.reduce((s, o) => s + o.normal_count, 0),
        unknown: overview.value.reduce((s, o) => s + o.unknown_count, 0),
      }
    }
    if (activeDeviceType.value === 'leak') {
      return {
        total: overview.value.reduce((s, o) => s + o.leak_total_points, 0),
        abnormal: overview.value.reduce((s, o) => s + o.leak_abnormal_count, 0),
        caution: 0,
        normal: overview.value.reduce((s, o) => s + o.leak_normal_count, 0),
        unknown: overview.value.reduce((s, o) => s + o.leak_unknown_count, 0),
      }
    }
    return { total: 0, abnormal: 0, caution: 0, normal: 0, unknown: 0 }
  })

  /** 타입별 통계 (전체 탭용) */
  const typeStats = computed(() => {
    const calc = (filterFn) => {
      const list = devices.value.filter(filterFn)
      const counts = { total: list.length, abnormal: 0, caution: 0, normal: 0, delay: 0 }
      for (const d of list) {
        const st = getDisplayStatus(d)
        counts[st] = (counts[st] || 0) + 1
      }
      return counts
    }
    return {
      pressure: calc(d => d.measure_type !== 'leak'),
      leak:     calc(d => d.measure_type === 'leak'),
      // flow 는 별도 데이터 소스(flowList) — typeStats 도 flowList 기반으로 집계
      flow:     (() => {
        const list = flowList.value || []
        const counts = { total: list.length, abnormal: 0, caution: 0, normal: 0, delay: 0 }
        for (const d of list) {
          const st = getDisplayStatus(d)
          counts[st] = (counts[st] || 0) + 1
        }
        return counts
      })(),
    }
  })

  /** 장비 유형 + 검색 + 상태 필터가 적용된 장비 목록 */
  const filteredDevices = computed(() => {
    let list = [...devices.value]

    // 장비 유형 필터
    if (activeDeviceType.value === 'all') {
      // 전체 — 유형 필터 없음
    } else if (activeDeviceType.value === 'pressure') {
      list = list.filter(d => d.measure_type !== 'leak')
    } else if (activeDeviceType.value === 'leak') {
      list = list.filter(d => d.measure_type === 'leak')
    } else {
      // 유량 — 별도 데이터 소스 (api/API21/flow/list)
      list = [...(flowList.value || [])]
    }

    // 상태 필터 — tableStatusVisible 의 false 인 상태만 제외
    const sv = tableStatusVisible.value || {}
    list = list.filter(d => sv[getDisplayStatus(d)] !== false)

    // 검색
    if (tableSearch.value) {
      const q = tableSearch.value.toLowerCase()
      list = list.filter(d =>
        d.point_name.toLowerCase().includes(q) ||
        (d.customer_name || '').toLowerCase().includes(q) ||
        (d.country_code || '').toLowerCase().includes(q),
      )
    }

    // 상태 우선순위 정렬
    const order = { abnormal: 0, caution: 1, delay: 2, normal: 3 }
    list.sort((a, b) => (order[getDisplayStatus(a)] ?? 4) - (order[getDisplayStatus(b)] ?? 4))

    return list
  })

  /** 지도에 표시할 장비 (좌표 있는 것만, 유형 필터 적용) */
  const mapDevices = computed(() => {
    return filteredDevices.value.filter(d => d.latitude != null && d.longitude != null)
  })

  // ─────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────

  function getDisplayStatus(d) {
    return getDeviceStatus(d)
  }

  // ─────────────────────────────────────────
  // Actions
  // ─────────────────────────────────────────

  async function loadOverview() {
    try {
      overview.value = await fetchOverview()
    } catch (e) {
      error.value = e.message || 'Failed to load overview'
    }
  }

  async function selectCountry(code) {
    if (selectedCountry.value === code) return
    selectedCountry.value = code
    selectedCustomer.value = null
    devices.value = []
    detail.value = []
    locations.value = []
    loading.value = true
    error.value = null
    try {
      const [detailData, locationData] = await Promise.all([
        fetchDetail(code),
        fetchLocations(code),
      ])
      detail.value = detailData
      locations.value = locationData
    } catch (e) {
      error.value = e.message
    } finally {
      loading.value = false
    }
  }

  async function selectCustomer(name) {
    if (!selectedCountry.value) return
    selectedCustomer.value = name
    tableStatusVisible.value = { abnormal: true, caution: true, normal: true, delay: true }
    loading.value = true
    try {
      const raw = await fetchDevices(selectedCountry.value, name)
      // customer_name / country_code 보강
      devices.value = raw.map(d => ({
        ...d,
        customer_name: name,
        country_code: selectedCountry.value,
      }))
    } catch {
      devices.value = []
    } finally {
      loading.value = false
    }
  }

  /**
   * 유량(flow) 지점 리스트 로드 — 레거시 api/API21/flow/list 호출
   *   - customer_id 는 sessionStorage.select_customer_id 기준 (== '27' 이면 1 로 치환)
   *   - search 는 tableSearch 값으로 자동 전달
   *   - 진행중인 요청은 AbortController 로 취소
   */
  async function loadFlowList(opts = {}) {
    let customerId = sessionStorage.select_customer_id
    if (customerId === '27') customerId = 1
    if (customerId == null || customerId === '') {
      flowList.value = []
      return
    }
    if (flowAbort) flowAbort.abort()
    flowAbort = new AbortController()
    flowLoading.value = true
    // to 파라미터 — 호출측이 'YYYY-MM-DD' 또는 ISO 일자를 넘기면 그날 00:00:00 으로 변환
    //   미지정 시 오늘 00:00:00
    let toStr = opts.to
    if (toStr) {
      const d = String(toStr).slice(0, 10)
      toStr = `${d} 00:00:00`
    } else {
      const dNow = new Date()
      toStr = `${dNow.getFullYear()}-${String(dNow.getMonth()+1).padStart(2,'0')}-${String(dNow.getDate()).padStart(2,'0')} 00:00:00`
    }
    const todayMidnight = toStr
    try {
      const list = await fetchFlowList({
        customerId,
        search: tableSearch.value || '',
        sort: flowSort.value || '',
        type: pointStatus.value || '',
        to: todayMidnight,
      }, flowAbort.signal)
      // device 형태로 정규화 — 좌측 리스트/지도/통계가 device 처럼 다룰 수 있게
      //   API 응답 필드 alias:
      //     night_min_flow → mnf (좌측 리스트/차트 표출 값)
      //     water_flow     → flow (분별 raw 값)
      flowList.value = (list || []).map(d => ({
        ...d,
        measure_type: 'flow',
        // 좌측 리스트/지도가 기대하는 필드명으로 fallback 매핑
        point_name: d.point_name ?? d.name ?? d.title ?? '',
        latitude:   d.latitude   ?? d.y ?? null,
        longitude:  d.longitude  ?? d.x ?? null,
        customer_name: d.customer_name ?? selectedCustomer.value ?? '',
        country_code:  d.country_code  ?? selectedCountry.value ?? '',
        // 표출용 필드 alias — 기존 mnf/flow 값이 이미 있으면 보존
        mnf:  d.mnf  ?? d.night_min_flow ?? null,
        flow: d.flow ?? d.water_flow     ?? null,
      }))
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.name === 'AbortError') return
      flowList.value = []
    } finally {
      flowLoading.value = false
    }
  }

  async function loadAllCustomers(code) {
    /** 한 국가의 모든 고객사 장비를 한번에 로드 */
    if (detail.value.length === 0) return
    loading.value = true
    try {
      const results = await Promise.all(
        detail.value.map(c =>
          fetchDevices(code, c.customer_name).then(raw =>
            raw.map(d => ({
              ...d,
              customer_name: c.customer_name,
              country_code: code,
            })),
          ),
        ),
      )
      devices.value = results.flat()
      selectedCustomer.value = '__all__'
    } catch {
      devices.value = []
    } finally {
      loading.value = false
    }
  }

  async function refreshCountry(code) {
    try {
      const [detailData, locationData] = await Promise.all([
        fetchDetail(code),
        fetchLocations(code),
      ])
      detail.value = detailData
      locations.value = locationData
      if (selectedCustomer.value && selectedCustomer.value !== '__all__') {
        const raw = await fetchDevices(code, selectedCustomer.value)
        devices.value = raw.map(d => ({ ...d, customer_name: selectedCustomer.value, country_code: code }))
      }
    } catch (e) {
      error.value = e.message
    }
  }

  function selectDevice(device, source = '') {
    selectedDevice.value = device
    selectedDeviceSource.value = source
  }

  function closeDeviceChart() {
    selectedDevice.value = null
    selectedDeviceSource.value = ''
  }

  function focusDeviceOnMap(device) {
    mapFocusDevice.value = device ? { ...device, _focusTs: Date.now() } : null
  }

  function addAiMessage(text) {
    aiMessages.value.push({
      role: 'user',
      text,
      time: new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }),
    })
    // 간단한 AI 응답 시뮬레이션
    setTimeout(() => {
      const stats = globalStats.value
      let reply = `현재 ${activeDeviceType.value === 'pressure' ? '수압' : '누수음'} 장비 ${stats.total}개 중 `
      reply += `이상 ${stats.abnormal}개, 주의 ${stats.caution}개, 정상 ${stats.normal}개입니다.`
      if (stats.abnormal > 0) reply += ` 이상 장비 점검이 필요합니다.`
      aiMessages.value.push({
        role: 'ai',
        text: reply,
        time: new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }),
      })
    }, 800)
  }

  return {
    // state
    selectedCountry, selectedCustomer,
    overview, detail, devices, locations,
    flowList, flowLoading, flowSort, pointStatus,
    activeDeviceType, tableSearch, tableStatusVisible, visibleColumns,
    aiMessages, loading, error,
    selectedDevice, selectedDeviceSource,
    mapFocusDevice,
    chartSlotsByType, slotMapVisible,
    // computed
    globalStats, typeStats, filteredDevices, mapDevices,
    // actions
    loadOverview, selectCountry, selectCustomer, loadAllCustomers,
    refreshCountry, selectDevice, closeDeviceChart,
    focusDeviceOnMap, addAiMessage, getDisplayStatus,
    setAllDevices, findDeviceByPointId,
    loadFlowList, setChartSlot, resetChartSlots,
    toggleSlotMapVisible,
    chartExpanded, setChartExpanded,
    MULTI_CHART_MAX, multiChartMode, multiChartSelectedIds,
    setMultiChartMode, toggleMultiChartId, setMultiChartIds, clearMultiChartIds,
  }
})
